/* eslint-disable @typescript-eslint/explicit-function-return-type */
import * as Yup from 'yup';
import { date } from 'yup';

import { LeaveTypeCodes } from '@Enum/index';
import dayjs from 'dayjs';
import { ValidationSetting } from '@Types/api/form';
import { getOrdinalSuffix } from '@Utils/getOrdinalSuffix';
import findValidationSetting from './utils/findValidationSetting';

export const getLeaveRequestFormSchema = (
  isFileAttached: boolean,
  isStaff: boolean,
  validationSettings: Array<ValidationSetting>
) =>
  Yup.object({
    document: Yup.string()
      .nullable()
      .test(
        'check that file is attached',
        'Please attach document',
        (_value, context) => {
          const { leaveTypeID } = context.parent;

          if (Number(leaveTypeID) === LeaveTypeCodes.SICK_LEAVE) {
            return isFileAttached;
          }

          return true;
        }
      ),
    startDate: date()
      .required('Please enter Leave Start Date')
      .test({
        name: 'startDate-common-rule',
        test(value, context) {
          const validationParmId2 = findValidationSetting(
            2,
            validationSettings
          );
          const validationParmId3 = findValidationSetting(
            3,
            validationSettings
          );

          if (validationParmId2?.enabled && validationParmId3?.enabled) {
            const currentYear = dayjs().year();

            const limitDate = dayjs()
              .set('year', currentYear + 1)
              .set('month', validationParmId2.value - 1)
              .set('date', validationParmId3.value);

            const startDate = dayjs(value);

            if (startDate.isAfter(limitDate)) {
              return context.createError({
                message: `Can not choose leave start date, after Next year ${limitDate.format('MMMM DD')}.!`,
              });
            }
          }

          return true;
        },
      })
      .test({
        name: 'startDate-rule-1',
        test(value, context) {
          const validationParmId4 = findValidationSetting(
            4,
            validationSettings
          );
          const validationParmId5 = findValidationSetting(
            5,
            validationSettings
          );
          const validationParmId6 = findValidationSetting(
            6,
            validationSettings
          );

          if (
            Number(context.parent.leaveTypeID) === LeaveTypeCodes.ANNUAL_LEAVE
          ) {
            const startDay = dayjs(value).date();
            const currentDate = dayjs();
            const totalDays = dayjs(value).diff(currentDate, 'day') + 1;
            const errorMessage = (contextValue: number) =>
              `Kindly apply annual leave before ${contextValue} days or proceed with emergency leave!`;

            if (validationParmId5?.enabled) {
              if (isStaff && totalDays < validationParmId5.value) {
                return context.createError({
                  message: errorMessage(validationParmId5.value),
                });
              }
            }

            if (!isStaff) {
              if (validationParmId6?.enabled) {
                if (totalDays < validationParmId6.value) {
                  return context.createError({
                    message: errorMessage(validationParmId6.value),
                  });
                }
              }
              if (validationParmId4?.enabled) {
                if (startDay < validationParmId4.value) {
                  const suffix = getOrdinalSuffix(validationParmId4.value);
                  return context.createError({
                    message: `Kindly apply annual leave after ${validationParmId4.value}${suffix} of this month..!`,
                  });
                }
              }
            }
          }

          return true;
        },
      }),
    endDate: date()
      .required('Please enter Leave End Date')
      .test(
        'endDate-rule-1',
        'End Date should be NOT less than Start Date!',
        (value, context) => {
          const { startDate } = context.parent;

          if (!startDate) {
            return true;
          }

          const chosenDateLessThanStartDate =
            dayjs(value).startOf('day') < dayjs(startDate).startOf('day');

          return !chosenDateLessThanStartDate;
        }
      ),
    totDays: Yup.string()
      .trim()
      .min(1, 'Total days not calculated!')
      .required(),
    ticketRequired: Yup.number().required(),
    passportRequired: Yup.number().required(),
    remarks: Yup.string().test(
      'If leave type is "Emergency leave" remarks field should be required',
      'Please fill remakrs',
      (value, context) => {
        const { leaveTypeID } = context.parent;

        if (leaveTypeID !== String(LeaveTypeCodes.EMERGENCY_LEAVE)) {
          return true;
        }

        return !!value;
      }
    ),
  });
